import '@/styles/globals.css';
import Script from 'next/script';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import TagManager from 'react-gtm-module';

import { useDispatch } from 'react-redux';
import GlobalContext from '@/contexts/global';
import Theme from 'Components/common/Theme';
import RouteLoader from '@/components/common/routeLoader';
import { Provider } from 'react-redux';
import createStore from '@/store/store';
import '@/utils/api-interceptor';
import '@/styles/globals.css';
import { NextPageContext } from 'next';
import {
    getFeatureValue,
    isGrowthBookReady,
    loadGrowthbookFeatures,
} from '@/utils/growthbook';
import growthbook from '@/utils/growthbook';
import GlobalLoader from '@/components/common/globalLoader';
import { initAckoAnalytics } from '@acko-tech/building-blocks.ui.acko-analytics';
//import '../public/fonts/euclid/fonts.css';
//import GlobalStyle from '@/styles/globalStyles';
import { Toaster } from 'react-hot-toast';
import Cookies from 'js-cookie';
import useUserData from '@/hooks/useUserData';
import { AppContext } from 'next/app';
import React, { useEffect } from 'react';
import { consoleLog } from '@/utils/api';
import { getUserId } from '@/utils';
import { useRouter } from 'next/router';
import { isNonIndexableRoute, isIndexableRoute, setReferrer } from '@/utils';
import { isClient } from '@/utils/common';
import { GOOGLE } from '@/constants';

interface PageProps {
    initialState?: {};
    isMobile: boolean;
    isRobot: boolean;
}
interface RootProps extends AppProps {
    pageProps: PageProps;
}

declare global {
    interface Window {
        flutter_inappwebview: any;
        dataLayer?: Record<string, any>[];
    }
}

export const DeviceContext = React.createContext({
    isMobile: true,
    isRobot: false,
});

export const detectRobot = (userAgent: string): boolean => {
    const robots = new RegExp(
        (
            [
                /bot/,
                /spider/,
                /crawl/,
                /APIs-Google/,
                /AdsBot/,
                /Googlebot/,
                /facebookcatalog/,
                /facebookexternalhit/,
                /mediapartners/,
                /Google Favicon/,
                /FeedFetcher/,
                /Google-Read-Aloud/,
                /DuplexWeb-Google/,
                /googleweblight/,
                /bing/,
                /yandex/,
                /baidu/,
                /duckduck/,
                /yahoo/,
                /ecosia/,
                /ia_archiver/,
                /facebook/,
                /instagram/,
                /pinterest/,
                /reddit/,
                /slack/,
                /twitter/,
                /whatsapp/,
                /youtube/,
                /semrush/,
            ] as RegExp[]
        )
            .map((r) => r.source)
            .join('|'),
        'i'
    );

    return robots.test(userAgent);
};

export default function App({ Component, pageProps }: RootProps) {
    const router = useRouter();
    // Since all routes now have /service-centre as root URL by default,
    // use the new function to determine indexability based on route structure
    const isIndexable = isIndexableRoute(router?.pathname || '');
    const title = 'Acko Drive Service Center';
    const { initialState = {}, ...restPageProps } = pageProps || {};
    const store = createStore(initialState);
    const { userDetails } = useUserData(store);
    const isProd = process.env.NEXT_PUBLIC_ENV?.toLowerCase() === 'production';
    const gtmId = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID;
    //isProd && typeof window !== 'undefined' ? (console.log = () => {}) : null;
    if (isProd && isClient()) {
        // Suppress console.log
        console.log = () => {};

        // Preserve original console.error
        const originalConsoleError = console.error;

        // Override console.error to also call consoleLog
        console.error = (...args) => {
            originalConsoleError(...args);

            const combined = args
                .map((a) =>
                    typeof a === 'object' ? JSON.stringify(a) : String(a)
                )
                .join(' ');

            consoleLog(combined);
        };
    }

    if (isClient()) {
        if (document.referrer.includes(GOOGLE)) {
            setReferrer(GOOGLE);
        }
    }
    // useEffect(() => {
    //     growthbook.setAttributes({
    //         id: Cookies.get('trackerid'),
    //     });
    //     growthbook.loadFeatures();
    // }, []);

    // useEffect(() => {
    //     setTimeout(() => {
    //         const isGrowthBookReadyy = isGrowthBookReady();
    //         console.log('^^^^^ isGrowthBookReadyy', { isGrowthBookReadyy });
    //         const isPresent = getFeatureValue({ flagName: 'garage-new-flow' });
    //         console.log('isPresent', { isPresent });
    //     }, 2000);
    // }, []);
    useEffect(() => {
        // consoleLog(`Logging client cookies ${userDetails.name}`); //Note:- Remove this post migration
        if (isProd && gtmId) {
            TagManager.initialize({ gtmId });
        }
        const handleUnload = () => {
            // Do nothing - this disables bfcache
        };
        window.addEventListener('unload', handleUnload);

        return () => {
            window.removeEventListener('unload', handleUnload);
        };
    }, []);

    return (
        <Provider store={store}>
            <DeviceContext.Provider value={pageProps}>
                <GlobalContext.Provider value={{ userDetails }}>
                    <Theme>
                        <>
                            <Head>
                                {!isIndexable && (
                                    <meta
                                        name="robots"
                                        content={'noindex, nofollow'}
                                    />
                                )}
                                <title>{title}</title>

                                <Script
                                    type="text/javascript"
                                    dangerouslySetInnerHTML={{
                                        __html: initAckoAnalytics(
                                            getUserId(),
                                            process.env
                                                .NEXT_PUBLIC_SEGMENT_KEY || ''
                                        ),
                                    }}
                                />

                                {isProd && (
                                    <>
                                        <Script
                                            src={`${process.env.NEXT_PUBLIC_ENV_PREFIX}/js/newrelic.js`}
                                            type="text/javascript"
                                            async
                                        />
                                        <script
                                            src={`${process.env.NEXT_PUBLIC_ENV_PREFIX}/js/clarity.js`}
                                            type="text/javascript"
                                        />
                                    </>
                                )}
                                {/* <script
                                    async
                                    src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_PLACES_KEY}&amp&libraries=places`}
                                /> */}
                            </Head>
                            {/* <GlobalStyle /> */}
                            <RouteLoader>
                                <Component {...restPageProps} />
                                <Toaster
                                    position="bottom-center"
                                    toastOptions={{
                                        duration: 3000,
                                        error: {
                                            style: {
                                                background: 'black',
                                                borderRadius: '8px',
                                                color: 'white',
                                            },
                                        },
                                    }}
                                />
                            </RouteLoader>
                        </>
                    </Theme>
                </GlobalContext.Provider>
            </DeviceContext.Provider>
        </Provider>
    );
}

App.getInitialProps = async ({ Component, ctx }: AppContext) => {
    const userAgent: any = ctx.req
        ? ctx.req.headers['user-agent']
        : !!navigator.userAgent
          ? navigator.userAgent
          : '';
    let isMobile = userAgent
        ? userAgent.match(
              /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
          )
        : true;
    return {
        pageProps: {
            ...(Component.getInitialProps
                ? await Component.getInitialProps(ctx)
                : {}),
            pathname: ctx.pathname,
            isMobile: Boolean(isMobile),
            isRobot: detectRobot(userAgent),
        },
    };
};

// App.getInitialProps = async ({ Component, ctx }: any) => {
//     const { query, res, req } = ctx;
//     console.log('ctx?.req', req.cookies.trackerid);

//     if (ctx?.req) {
//         let trackerid = Cookies.get('trackerid') || '';
//         console.log('trackerid', req.cookies.trackerid);
//         growthbook.setAttributes({
//             id: req.cookies.trackerid,
//         });
//     }
//     await growthbook.loadFeatures();

//     const isGrowthBookReadyy = isGrowthBookReady();
//     console.log('^^^^^ isGrowthBookReadyy', { isGrowthBookReadyy });
//     const isPresent = getFeatureValue({ flagName: 'garage-new-flow' });
//     console.log('isPresent', { isPresent });
//     return {};
// };
